import { fileAvailability, fileClassFromContentType, isFileUid } from '@/utils/fileUrl';

/**
 * Credential documents on the Documents page and the profile: what may be
 * picked, and how keyon's ListCredentialDocuments items become the rows those
 * pages render.
 */

// keyon's credential domain: image (no gif), pdf, and office/text files, which
// are held for a malware scan and only ever downloaded.
export const CREDENTIAL_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv';

// keyon/lib/FileValidator.php MAX_BYTES.
export const MAX_CREDENTIAL_BYTES = 20 * 1024 * 1024;

export const CREDENTIAL_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

const ALLOWED_EXTENSIONS = CREDENTIAL_ACCEPT.split(',').map((e) => e.slice(1));

// Browsers report no type, or a generic one, for files they do not recognise
// (a .csv on a machine without Excel, a PDF from some scanners). Only then does
// the extension decide; keyon sniffs the bytes either way.
const GENERIC_TYPES = ['', 'application/octet-stream'];

function extensionOf(name) {
  const base = String(name || '');
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

/** @returns {{ok:true}|{ok:false,error:string}} */
export function validateCredentialFile(file) {
  if (!file) return { ok: false, error: 'Choose a file to upload.' };
  const type = String(file.type || '').toLowerCase();
  const typeAllowed = CREDENTIAL_MIME_TYPES.includes(type)
    || (GENERIC_TYPES.includes(type) && ALLOWED_EXTENSIONS.includes(extensionOf(file.name)));
  if (!typeAllowed) {
    return {
      ok: false,
      error: 'Invalid file type. Please upload a PDF, JPG, PNG, WEBP, DOC, DOCX, XLS, XLSX, TXT or CSV file.',
    };
  }
  if (typeof file.size !== 'number' || file.size > MAX_CREDENTIAL_BYTES) {
    return { ok: false, error: 'File size exceeds the 20MB limit.' };
  }
  return { ok: true };
}

/** The page has three sections; misc, other and anything unknown share the third. */
export function sectionForCategory(category) {
  return category === 'license' || category === 'insurance' ? category : 'misc';
}

const EXTENSION_BY_TYPE = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

/**
 * keyon items → page rows.
 * `fileClass` comes from the stored content type, not from the category (a
 * category `other` row is mostly PDFs and images) nor from the name.
 * `uploadedAt` is keyon's `created_at` text, passed through unchanged.
 * In the org view each name is prefixed with its owner, since rows from many
 * people share one list.
 *
 * @param {object[]} items flattened CredentialItems from listCredentialDocuments
 * @param {{scope?: 'own'|'org'}} options
 */
export function mapCredentialItems(items, { scope = 'own' } = {}) {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item && isFileUid(item.file_uid))
    .map((item) => {
      const name = String(item.name || 'document');
      const contentType = String(item.content_type || '');
      const owner = String(item.owner_unique_id || '');
      return {
        id: item.file_uid,
        fileUid: item.file_uid,
        name: scope === 'org' ? `${item.owner_name || owner} — ${name}` : name,
        type: sectionForCategory(item.category),
        category: String(item.category || ''),
        extension: extensionOf(name) || EXTENSION_BY_TYPE[contentType.toLowerCase()] || '',
        contentType,
        fileClass: fileClassFromContentType(contentType),
        size: typeof item.byte_size === 'number' ? item.byte_size : Number(item.byte_size) || 0,
        uploadedAt: item.created_at,
        ownerUniqueId: owner,
        availability: fileAvailability(item),
      };
    });
}
